export type NovaModelLifecycleState = 'ACTIVE' | 'DEPRECATED' | 'RETIRED';

// The fields the consuming surfaces actually read. The service serves more (output ceiling, input
// capacity, effort, residency) but none of it is rendered, so it is not requested — keeping the
// fetched shape, and the nullable surface it has to be validated against, to only what is used.
export interface NovaModel {
	modelId: string;
	displayName: string;
	hint: string;
	lifecycleState: NovaModelLifecycleState;
}

export interface NovaModelListData {
	models: NovaModel[];
	defaultModelId: string | null;
}

interface RawNovaModel {
	readonly modelId: string | null;
	readonly displayName: string | null;
	readonly hint: string | null;
	readonly lifecycleState: string | null;
}

function isNovaModelLifecycleState(value: unknown): value is NovaModelLifecycleState {
	return value === 'ACTIVE' || value === 'DEPRECATED' || value === 'RETIRED';
}

// The list is proxied verbatim from an externally-owned registry, so every field arrives nullable and
// a value outside the frozen set is possible. Same posture as the variable library's mapper: drop
// what cannot be used, default what can.
export function mapNovaModelList(
	raw:
		| {
				readonly models: ReadonlyArray<RawNovaModel | null> | null;
				readonly defaultModelId: string | null;
		  }
		| null
		| undefined
): NovaModelListData {
	const models: NovaModel[] = [];
	for (const model of raw?.models ?? []) {
		// Without an id a model can be neither selected nor matched against a saved value, so there
		// is nothing a row could usefully do.
		if (!model || !model.modelId) {
			continue;
		}
		const lifecycleState = isNovaModelLifecycleState(model.lifecycleState) ? model.lifecycleState : 'ACTIVE';
		// A retired model is refused by the service on every save (`validateCreateInput` throws
		// `model '<id>' is retired and cannot be saved`), so an offered retired row can only ever
		// dead-end the agent an author picks it for. The endpoint withholds them already
		// (`listOfferedModels`), but that is a service-side convention which neither this type nor the
		// GraphQL enum enforces — so the offer set is narrowed here rather than assumed. Keeping the
		// state on the type is still useful; offering it is not.
		if (lifecycleState === 'RETIRED') {
			continue;
		}
		models.push({
			modelId: model.modelId,
			// The id rather than a blank: a nameless row in a dropdown is unpickable in practice.
			displayName: model.displayName ?? model.modelId,
			hint: model.hint ?? '',
			lifecycleState,
		});
	}
	return {models, defaultModelId: raw?.defaultModelId ?? null};
}

export function findNovaModel(models: NovaModel[], modelId: string | null): NovaModel | null {
	if (!modelId) {
		return null;
	}
	return models.find(model => model.modelId === modelId) ?? null;
}

// The display name where one is known, the raw id otherwise. The fallback is the normal case for a
// historical run recorded against a since-retired model: the endpoint serves offered models only, so
// the id is the most truthful thing left to show.
export function describeNovaModel(models: NovaModel[], modelId: string | null): string {
	if (!modelId) {
		return '';
	}
	return findNovaModel(models, modelId)?.displayName ?? modelId;
}

// Whether a saved model can still be chosen. No selection at all counts as offered: the picker shows
// its placeholder for that, not an "unavailable" warning.
export function isNovaModelOffered(models: NovaModel[], modelId: string | null): boolean {
	if (!modelId) {
		return true;
	}
	return findNovaModel(models, modelId) !== null;
}

// Whether a saved model is *known* to be unavailable — the one question both the picker's warning
// and the Save gate ask, so they can never disagree. An empty list is deliberately not an answer:
// `viewer.novaModels` is `getModels(context).catch(() => null)`, so a registry blip serves no models
// at all, and reading that as "nothing is offered" would warn on every agent and block every save
// for the duration of the blip.
export function isNovaModelUnavailable(models: NovaModel[], modelId: string | null): boolean {
	return models.length > 0 && !isNovaModelOffered(models, modelId);
}
